<template>
    <Teleport to="body">
        <Transition name="sidebar-fade">
            <button v-if="open" type="button" class="fixed inset-0 z-40 w-full cursor-default bg-black/40 md:hidden" aria-label="Close goal form" @click="handleCancel" />
        </Transition>
        <!-- FIXME: Use BaseDrawer when it preserves non-modal desktop interaction, mobile geometry, and focus return. -->
        <Transition name="sidebar-slide">
            <FocusScope as-child loop trapped>
                <div
                    v-if="open"
                    class="fixed inset-x-0 bottom-0 z-50 flex w-full max-h-[90dvh] flex-col rounded-t-2xl border-t border-border bg-surface shadow-2xl pb-[var(--mobile-nav-total-height,0px)] md:inset-y-0 md:right-0 md:left-auto md:w-[440px] md:max-h-none md:rounded-none md:border-t-0 md:border-l md:pb-0"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="goal-edit-sidebar-title">
                    <!-- Header -->
                    <div class="flex items-center justify-between gap-3 px-6 py-5 border-b border-border">
                        <h2 id="goal-edit-sidebar-title" class="body text-ink min-w-0 flex-1 truncate">
                            {{ isEdit ? "Edit Goal" : "New Goal" }}
                        </h2>
                        <BaseButton theme="tertiary" size="sm" icon-only aria-label="Close" :disabled="isSubmitting" @click="handleCancel">
                            <i class="bi bi-x-lg text-sm" aria-hidden="true"></i>
                        </BaseButton>
                    </div>

                    <!-- Body -->
                    <div class="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
                        <!-- Title -->
                        <div class="flex flex-col gap-2">
                            <label for="goal-title-input" class="w-full flex flex-col gap-2">
                                <span class="scaffold-body-small text-control-label">Title</span>
                                <BaseInput id="goal-title-input" v-model="form.title" placeholder="What do you want to achieve?" :disabled="isSubmitting" />
                            </label>
                        </div>

                        <!-- Description -->
                        <div class="flex flex-col gap-2">
                            <label for="goal-description-textarea" class="w-full flex flex-col gap-2">
                                <span class="scaffold-body-small text-control-label">Description</span>
                                <BaseTextarea
                                    id="goal-description-textarea"
                                    v-model:input="form.description"
                                    rows="7"
                                    disable-autosize
                                    :disabled="isSubmitting"
                                    placeholder="Additional details will help Nadia coach you toward success."
                                    class="resize-none rounded-md border border-control-border bg-control-bg p-2.5 text-control-text placeholder:text-control-placeholder"
                                    :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'" />
                            </label>
                        </div>

                        <!-- Assignee -->
                        <div v-if="requireConnection || fixedConnection" class="flex flex-col gap-2">
                            <span class="scaffold-body-small text-control-label">Assignee</span>

                            <!-- Fixed (read-only) -->
                            <div v-if="fixedConnection" class="flex flex-col gap-1">
                                <p class="nadia-title-subsection text-ink">{{ fixedConnection.name }}</p>
                                <p v-if="fixedConnection.job_title" class="body-small text-ink-muted">{{ fixedConnection.job_title }}</p>
                            </div>

                            <!-- Selectable -->
                            <template v-else>
                                <div v-if="form.connection" class="flex items-start justify-between gap-2">
                                    <div class="flex flex-col gap-1">
                                        <p class="nadia-title-subsection text-ink">{{ form.connection.name }}</p>
                                        <p v-if="form.connection.job_title" class="body-small text-ink-muted">{{ form.connection.job_title }}</p>
                                    </div>
                                    <BaseButton theme="tertiary" size="sm" :disabled="isSubmitting" @click="form.connection = null"> Change </BaseButton>
                                </div>
                                <ConnectionSearch v-else :disabled="isSubmitting" @select="form.connection = $event" />
                            </template>
                        </div>
                    </div>

                    <!-- Priority toggle (new personal goals only) -->
                    <div v-if="canPrioritize && !isEdit" class="px-6 pb-4">
                        <GoalPriorityToggleButton
                            :is-prioritized="form.isPriority"
                            :disabled="isSubmitting"
                            prioritized-text="Make this a priority goal"
                            @click="form.isPriority = !form.isPriority" />
                    </div>

                    <!-- Footer -->
                    <div class="border-t border-border">
                        <div class="flex items-center justify-end gap-3 px-6 py-4">
                            <BaseButton theme="neutral" size="sm" :disabled="isSubmitting" @click="handleCancel">Cancel</BaseButton>
                            <BaseButton theme="primary" size="sm" :disabled="!isValid || isSubmitting" @click="handleSubmit">
                                <i v-if="isSubmitting" class="bi bi-arrow-clockwise animate-spin" aria-hidden="true"></i>
                                {{ isEdit ? "Save changes" : "Add goal" }}
                            </BaseButton>
                        </div>
                    </div>
                </div>
            </FocusScope>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { useEventListener } from "@vueuse/core";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { type GoalConnection, type GoalCreateEditData, GoalType } from "~vue/types/goals";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { FocusScope } from "reka-ui";
import { computed, reactive, watch } from "vue";

import ConnectionSearch from "./ConnectionSearch.vue";
import GoalPriorityToggleButton from "./GoalPriorityToggleButton.vue";

const props = defineProps<{
    initialData?: Partial<GoalCreateEditData>;
    isSubmitting?: boolean;
    requireConnection?: boolean;
    fixedConnection?: GoalConnection | null;
    canPrioritize?: boolean;
}>();

const emit = defineEmits<{
    submit: [data: GoalCreateEditData];
    close: [];
}>();

const open = defineModel<boolean>("open", { default: false });

const isEdit = computed(() => !!props.initialData?.id);

const form = reactive({
    title: props.initialData?.title ?? "",
    description: props.initialData?.description ?? "",
    goal_type: (props.initialData?.goal_type ?? GoalType.Development) as GoalType,
    connection: (props.initialData?.connection ?? null) as GoalConnection | null,
    isPriority: false,
});

watch(
    open,
    (val) => {
        if (val) {
            resetForm();
        }
    },
    { flush: "post" },
);

watch(
    () => props.isSubmitting,
    (val, prev) => {
        if (prev === true && val === false) {
            open.value = false;
        }
    },
);

const isValid = computed(() => form.title.trim() !== "" && ((!props.requireConnection && !props.fixedConnection) || form.connection !== null));

function cleanDescription(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

function resetForm() {
    form.title = props.initialData?.title ?? "";
    form.description = cleanDescription(props.initialData?.description ?? "");
    form.goal_type = props.initialData?.goal_type ?? GoalType.Development;
    form.connection = props.fixedConnection ?? props.initialData?.connection ?? null;
    form.isPriority = props.initialData?.is_prioritized ?? false;
}

function handleCancel() {
    if (props.isSubmitting) {
        return;
    }
    open.value = false;
    emit("close");
}

function onEscapeKey(e: KeyboardEvent) {
    if (e.key === "Escape" && open.value && !e.defaultPrevented && !document.querySelector("[data-dismissable-layer]")) {
        handleCancel();
    }
}
useEventListener("keydown", onEscapeKey);

function handleSubmit() {
    if (!isValid.value || props.isSubmitting) {
        return;
    }
    emit("submit", {
        id: props.initialData?.id,
        title: form.title.trim(),
        description: form.description.trim(),
        goal_type: form.goal_type,
        team_member_goal: props.initialData?.team_member_goal ?? false,
        connection_id: form.connection?.id ?? props.initialData?.connection_id ?? null,
        connection: form.connection ?? props.initialData?.connection ?? null,
        is_prioritized: props.canPrioritize && !isEdit.value ? form.isPriority : undefined,
    });
}
</script>

<style scoped>
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
    transform: translateY(100%);
}

@media (min-width: 768px) {
    .sidebar-slide-enter-from,
    .sidebar-slide-leave-to {
        transform: translateX(100%);
    }
}

.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
    transition: transform 300ms ease;
}

.sidebar-fade-enter-from,
.sidebar-fade-leave-to {
    opacity: 0;
}

.sidebar-fade-enter-active,
.sidebar-fade-leave-active {
    transition: opacity 200ms ease;
}
</style>
